import React, { useState, useMemo, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, Pressable, Linking } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import Input from '@/components/General/inputs/Input';
import Checkbox from '@/components/General/inputs/Checkbox';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import SelectInput from '@/components/General/inputs/SelectInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SolidButton from '@/components/General/SolidButton';
import { Mutation } from '@/utils/react-query/Mutations';
import * as Validator from '@/utils/InputValidator';
import { Toast } from 'react-native-toast-message/lib/src/Toast';
import { Query } from '@/utils/react-query/Queries';
import CustomField from '@/components/CustomFields/CustomField';
import QuickFormFieldsModal from './QuickFormFieldsModal';
import Unicon from '@/components/General/Unicon';
import { getPersonProfile } from '@/data/people';
import { sendOptIn } from '@/data/sms';
import InputSection from '../General/inputs/InputSection';
import { useQueryClient } from '@tanstack/react-query';
import ProfileCard from '@/components/People/ProfileCard';
import FamilyMemberRow, { FamilyPositionTrigger } from '@/components/People/FamilyMemberRow';
import DropdownMenu from '@/components/General/DropdownMenu';
import SheetModal from '@/components/General/SheetModal';
import DuplicateReviewModal from '@/components/People/DuplicateReviewModal';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { DateTime } from 'luxon';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';

const isEmptyFamilyValue = value => value == null || (typeof value === 'string' && !value.trim()) || (Array.isArray(value) && !value.length);
const cloneFamilyValue = value => value instanceof Date ? new Date(value.getTime()) : Array.isArray(value) ? value.map(cloneFamilyValue) : value && typeof value === 'object' ? Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneFamilyValue(item)])) : value;
const formatFamilyValue = (value, type) => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.map(item => formatFamilyValue(item, type)).join(', ');
    if (type === 'Date' || type === 'Datetime') {
        const date = value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(String(value));
        if (date.isValid) return date.toLocaleString(type === 'Date' ? DateTime.DATE_MED : DateTime.DATETIME_MED);
    }
    if (value && typeof value === 'object') return value.name || value.filename || 'File';
    return String(value ?? '');
};

const INITIAL_PERSON_DETAILS = {
    firstName: '',
    lastName: '',
    email: '',
    Phones: [{ phoneNumber: '', label: 'Mobile', smsOptInStatus: 'never_opted_in', private: false }],
    dateOfBirth: null,
    gender: null,
    invitedByID: null,
    InvitedBy: null,
    addressID: null,
    Address: null,
    Family: null,
    Roles: [],
    customFieldValues: {},
};

const AddPersonQuickForm = ({ onSuccess, buttonText = 'Create Person', showCustomizeButton = true, showAddAnother = true, showFamily = true, fromScreen, fromStoreKey, formKey = 'default', familySource }) => {
    // Form state lives in useEditScreenStore (not useState): SheetModal hides —
    // and therefore unmounts — its children when a pushed screen blurs the host
    // (e.g. SheetSelector "Add Address"). Store-backed state survives the remount
    // so the screen's dataChangeFunction can still deliver the new record.
    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData);
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData);
    const savedFormState = useEditScreenStore(s => s.QuickAddPersonModal?.[formKey]);
    const personDetails = savedFormState?.personDetails || INITIAL_PERSON_DETAILS;
    const familyMode = savedFormState?.familyMode ?? null;
    const showQuickAddFamily = savedFormState?.showQuickAddFamily ?? false;

    const setFormState = (patch) => {
        const current = useEditScreenStore.getState().QuickAddPersonModal?.[formKey] || {};
        setEditScreenData("QuickAddPersonModal", formKey, typeof patch === 'function' ? patch(current) : patch);
    };
    const setPersonDetails = (value) => setFormState(prev => ({
        personDetails: typeof value === 'function' ? value(prev.personDetails || INITIAL_PERSON_DETAILS) : value
    }));
    const setFamilyMode = (value) => setFormState({ familyMode: value });
    const setShowQuickAddFamily = (value) => setFormState({ showQuickAddFamily: value });

    const [showFieldsModal, setShowFieldsModal] = useState(false);
    const addAnotherRef = useRef(false);

    const queryClient = useQueryClient();
    const { data: enabledFields = [] } = Query.quickPersonFields.get();
    // Custom fields (and roles) are paid-only — skip the metadata query on Free.
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);
    const { data: customFields } = Query.personCustomFields.all({ options: { enabled: !isFree } });
    const { data: loggedInUser } = Query.people.loggedInUser();

    // Duplicate detection — this form only creates new people. Debounce the
    // entered name, then look up existing people with the same first + last
    // name (case-insensitive). The warning is dismissible per name pair.
    const [debouncedFirstName, setDebouncedFirstName] = useState('');
    const [debouncedLastName, setDebouncedLastName] = useState('');
    const [ignoredDuplicateKey, setIgnoredDuplicateKey] = useState(null);
    const [reviewPersonId, setReviewPersonId] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedFirstName(personDetails.firstName?.trim() || '');
            setDebouncedLastName(personDetails.lastName?.trim() || '');
        }, 400);
        return () => clearTimeout(t);
    }, [personDetails.firstName, personDetails.lastName]);

    const duplicateKey = debouncedFirstName && debouncedLastName
        ? `${debouncedFirstName.toLowerCase()}|${debouncedLastName.toLowerCase()}`
        : null;

    const { data: potentialDuplicates } = Query.people.byName({
        firstName: debouncedFirstName,
        lastName: debouncedLastName,
        options: { enabled: !!duplicateKey },
    });

    const visibleDuplicates = useMemo(() => {
        if(!potentialDuplicates?.length) return [];
        if(ignoredDuplicateKey === duplicateKey) return [];
        return potentialDuplicates;
    }, [potentialDuplicates, ignoredDuplicateKey, duplicateKey]);

    const updateField = (field, value) => {
        setPersonDetails(prev => ({ ...prev, [field]: value }));
    };

    const sourceName = [familySource?.firstName, familySource?.lastName].filter(Boolean).join(' ');
    const sourceRole = familySource?.Family?.FamilyMembers?.find(member => member.id === familySource.id)?.familyRole || familySource?.familyRole;
    const sourceLabel = sourceName ? `${sourceName}${sourceRole ? ` (${sourceRole})` : ''}` : sourceRole || 'Family';

    const familyCopySuggestion = (key, value, getValue, applyValue, displayValue = formatFamilyValue(value)) => {
        if (!familySource || isEmptyFamilyValue(value) || !isEmptyFamilyValue(getValue(personDetails))) return null;
        const label = `Use ${sourceLabel}: ${displayValue}`;
        return (
            <Pressable
                key={key}
                testID={`FamilyCopy-${key}`}
                accessibilityRole="button"
                accessibilityLabel={label}
                disabled={savePersonMutation.isPending}
                style={styles.familyCopySuggestion}
                onPress={() => setPersonDetails(prev => isEmptyFamilyValue(getValue(prev)) ? applyValue(prev, cloneFamilyValue(value)) : prev)}
            >
                <Unicon name="copy-outline" size={15} colorVariant="primary" />
                <Text style={styles.familyCopyText}>{label}</Text>
            </Pressable>
        );
    };

    const updatePhoneSMS = (enabled) => {
        setPersonDetails(prev => {
            const phones = [...(prev.Phones || [])];
            phones[0] = { ...phones[0], smsOptInStatus: enabled ? 'opted_in' : 'never_opted_in' };
            return { ...prev, Phones: phones };
        });
    };

    const formatPhoneNumber = (number) => {
        const input = number.replace(/\D/g, '').substring(0, 10);
        const areaCode = input.substring(0, 3);
        const middle = input.substring(3, 6);
        const last = input.substring(6, 10);

        if (input.length > 6) return `(${areaCode}) ${middle}-${last}`;
        if (input.length > 3) return `(${areaCode}) ${middle}`;
        if (input.length > 0) return `(${areaCode}`;
        return number;
    };

    const resetForm = () => {
        removeEditScreenData("QuickAddPersonModal", formKey);
    };

    const savePersonMutation = Mutation.people.save({
        onSuccess: (data, variables) => {
            // Send opt-in messages for phones newly marked opted_in — same
            // contract as people/create.jsx. This form only creates new
            // people, so any opted_in phone transitioned from never_opted_in.
            const newlyOptedIn = (variables.Phones || []).filter(np =>
                np.phoneNumber?.trim() && np.smsOptInStatus === 'opted_in'
            );
            for (const phone of newlyOptedIn) {
                sendOptIn({ personID: data.id, phoneNumber: phone.phoneNumber, previousStatus: 'never_opted_in' }).catch(() => {});
            }

            if (addAnotherRef.current) {
                queryClient.invalidateQueries();
                resetForm();
                addAnotherRef.current = false;
                onSuccess?.(data, variables);
                Toast.show({ type: 'success', text2: `${data.firstName} ${data.lastName} added successfully. Add another person below.` });
            } else {
                resetForm();
                onSuccess?.(data, variables);
            }
        },
    });

    const handleSubmit = (addAnother = false) => {
        if (!Validator.areStringsPopulated([personDetails.firstName, personDetails.lastName])) {
            Toast.show({ type: 'error', text2: 'A new person must have a first & last name' });
            return;
        }

        if (personDetails.email && !Validator.validateEmail(personDetails.email)) {
            Toast.show({ type: 'error', text2: 'Email address is not a valid email!' });
            return;
        }

        // Transform family data for backend - filter out the new person (id: undefined)
        // and map fields to what createPerson expects (personID, FamilyRole)
        const thisMember = personDetails.Family?.FamilyMembers?.find(member => !member.id);

        const transformedDetails = {
            ...personDetails,
            familyRole: thisMember?.familyRole || personDetails.familyRole,
            Family: personDetails.Family ? {
                ...personDetails.Family,
                FamilyMembers: personDetails.Family.FamilyMembers
                    ?.filter(member => member.id) // Filter out new person (id: undefined)
                    ?.map(member => ({
                        id: member.id,
                        familyRole: member.familyRole
                    })) || []
            } : null
        };

        addAnotherRef.current = addAnother;
        savePersonMutation.mutate(transformedDetails);
    };

    const isFieldEnabled = (fieldKey) => enabledFields.includes(fieldKey);

    const handleJoinExistingFamily = async (selectedPerson) => {
        const fullPerson = await getPersonProfile(selectedPerson.id);
        
        if (!fullPerson || !fullPerson?.Family?.FamilyMembers) {
            Toast.show({ type: 'error', text2: 'This person is not part of a family. Select someone who belongs to a family.' });
            return;
        }

        const thisPerson = {
            id: undefined,
            firstName: personDetails.firstName || 'New Person',
            lastName: personDetails.lastName || '',
            familyRole: 'Unassigned'
        };

        setPersonDetails(prev => ({
            ...prev,
            Family: {
                ...fullPerson.Family,
                FamilyMembers: [...fullPerson.Family.FamilyMembers, thisPerson]
            }
        }));
        setFamilyMode(null);
    };

    const handleQuickAddFamilyMember = (newPerson) => {
        setShowQuickAddFamily(false);
        
        const newMember = {
            id: newPerson.id,
            firstName: newPerson.firstName,
            lastName: newPerson.lastName,
            familyRole: 'Unassigned'
        };

        if (personDetails.Family) {
            setPersonDetails(prev => ({
                ...prev,
                Family: {
                    ...prev.Family,
                    FamilyMembers: [...(prev.Family.FamilyMembers || []), newMember]
                }
            }));
        } else {
            const thisPerson = {
                id: undefined,
                firstName: personDetails.firstName || 'New Person',
                lastName: personDetails.lastName || '',
                familyRole: 'Head of Household'
            };
            setPersonDetails(prev => ({
                ...prev,
                Family: {
                    FamilyMembers: [thisPerson, newMember]
                }
            }));
        }
    };

    const RelationshipInput = (familyMember) => {
        const onSelect = (new_value) => {
            const isAnotherFamilyMemberHeadOfHousehold = new_value == "Head of Household" && 
                personDetails.Family?.FamilyMembers?.findIndex(n => n.id !== familyMember.id && n.familyRole == "Head of Household") > -1;

            if (isAnotherFamilyMemberHeadOfHousehold) {
                Toast.show({ type: 'error', text2: "You cannot have more than one Head of Household in a family." });
                return;
            }

            const newFamily = [...(personDetails.Family?.FamilyMembers || [])];
            const currentFamilyIndex = newFamily.findIndex(n => n.id === familyMember.id);

            if (currentFamilyIndex > -1) {
                newFamily[currentFamilyIndex].familyRole = new_value;

                setPersonDetails(prev => ({
                    ...prev,
                    Family: {
                        ...prev.Family,
                        FamilyMembers: newFamily
                    }
                }));
            }
        };

        return (
            <DropdownMenu 
                testID="Family Position"
                triggerComponent={<FamilyPositionTrigger role={familyMember.familyRole} />}
                options={[
                    { name: "Head of Household", onSelect: (option) => onSelect(option.name) },
                    { name: "Spouse", onSelect: (option) => onSelect(option.name) },
                    { name: "Adult", onSelect: (option) => onSelect(option.name) },
                    { name: "Child", onSelect: (option) => onSelect(option.name) },
                    { name: "Unassigned", onSelect: (option) => onSelect(option.name) },
                ]}
            />
        );
    };

    const enabledCustomFields = useMemo(() => {
        if (isFree || !customFields) return [];
        return customFields.filter(cf => enabledFields.includes(`custom_${cf.id}`));
    }, [customFields, enabledFields, isFree]);

    return (
        <View style={styles.flex}>
            <KeyboardAwareScrollView 
                contentContainerStyle={styles.scrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
                bottomOffset={100}
            >
            <View style={styles.container}>
                {showCustomizeButton && (
                    <TouchableOpacity 
                        style={styles.customizeButton} 
                        onPress={() => setShowFieldsModal(true)}
                    >
                        <Unicon name="settings-outline" size={16} colorVariant="primary" />
                        <Text style={styles.customizeButtonText}>Customize Form</Text>
                    </TouchableOpacity>
                )}

                {visibleDuplicates.length > 0 && (
                    <View style={styles.duplicateWarningContainer} testID="DuplicateWarning">
                        <View style={styles.duplicateWarningHeader}>
                            <View style={styles.duplicateWarningTitleRow}>
                                <Unicon name="warning-outline" size={18} colorVariant="warning" />
                                <Text style={styles.duplicateWarningTitle}>
                                    Possible duplicate{visibleDuplicates.length > 1 ? 's' : ''} ({visibleDuplicates.length})
                                </Text>
                            </View>
                            <TouchableOpacity
                                testID="DuplicateWarningIgnore"
                                onPress={() => setIgnoredDuplicateKey(duplicateKey)}
                                style={styles.duplicateWarningIgnoreBtn}
                            >
                                <Unicon name="close" size={16} colorVariant="textSecondary" />
                                <Text style={styles.duplicateWarningIgnoreText}>Ignore</Text>
                            </TouchableOpacity>
                        </View>
                        <Text style={styles.duplicateWarningSubtitle}>
                            Someone with this name already exists. Review to make sure you aren&apos;t creating a duplicate.
                        </Text>
                        {visibleDuplicates.map((person) => (
                            <ProfileCard
                                key={person.id}
                                profile={person}
                                subtitle={person.email || (person.dateOfBirth ? `Born ${new Date(person.dateOfBirth).toLocaleDateString()}` : null)}
                                backgroundColorVariant="background"
                                testID={`DuplicateWarning-Person-${person.id}`}
                                onPress={() => setReviewPersonId(person.id)}
                            />
                        ))}
                    </View>
                )}

                <View style={styles.row}>
                    <View style={styles.halfInput}>
                        <Input
                            title="First Name"
                            autoComplete="given-name"
                            value={personDetails.firstName}
                            onUpdate={(value) => updateField('firstName', value)}
                        />
                    </View>
                    <View style={styles.halfInput}>
                        <Input
                            title="Last Name"
                            autoComplete="family-name"
                            value={personDetails.lastName}
                            onUpdate={(value) => updateField('lastName', value)}
                        />
                    </View>
                </View>

                {isFieldEnabled('email') && (
                    <View>
                        <Input
                            title="Email"
                            autoCapitalize="none"
                            type="email-address"
                            autoComplete="email"
                            value={personDetails.email}
                            onUpdate={(value) => updateField('email', value)}
                        />
                        {familyCopySuggestion('email', familySource?.email, person => person.email, (person, email) => ({ ...person, email }))}
                    </View>
                )}

                {isFieldEnabled('phone') && (
                    <View>
                        <Input
                            title="Phone"
                            type="phone-pad"
                            maxLength={14}
                            value={personDetails.Phones[0]?.phoneNumber || ''}
                            onUpdate={(value) => {
                                const phones = [...personDetails.Phones];
                                phones[0] = { ...phones[0], phoneNumber: formatPhoneNumber(value) };
                                updateField('Phones', phones);
                            }}
                        />

                        {familySource?.Phones?.map((phone, index) => familyCopySuggestion(
                            `phone-${index}`,
                            phone.phoneNumber,
                            person => person.Phones?.[0]?.phoneNumber,
                            (person, phoneNumber) => ({
                                ...person,
                                Phones: [{
                                    phoneNumber,
                                    label: phone.label || 'Mobile',
                                    smsOptInStatus: ['opted_out', 'non_mobile'].includes(phone.smsOptInStatus) ? phone.smsOptInStatus : 'never_opted_in',
                                    private: !!phone.private,
                                }, ...(person.Phones || []).slice(1)],
                            }),
                            `${phone.label || 'Phone'}: ${phone.phoneNumber}`
                        ))}

                        {!!personDetails.Phones[0]?.phoneNumber?.trim() && (
                            <View>
                                {personDetails.Phones[0]?.smsOptInStatus === 'opted_out' && (
                                    <View style={styles.phoneOptInRow}>
                                        <Text style={[styles.phoneDisclaimerText, { color: '#d97706' }]}>
                                            <Unicon name="warning-outline" size={12} colorVariant="warning" /> This number has opted out of SMS. Reply START to {personDetails.Phones[0]?.phoneNumber || 'this number'} to re-enable.
                                        </Text>
                                    </View>
                                )}
                                {personDetails.Phones[0]?.smsOptInStatus === 'non_mobile' && (
                                    <View style={styles.phoneOptInRow}>
                                        <Text style={[styles.phoneDisclaimerText, { color: '#6b7280' }]}>
                                            <Unicon name="phone-off" library="material" size={12} colorVariant="textSecondary" /> This number is not a mobile phone and cannot receive SMS messages.
                                        </Text>
                                    </View>
                                )}
                                {personDetails.Phones[0]?.smsOptInStatus === 'opt_in_paused' && (
                                    <View style={styles.phoneOptInRow}>
                                        <Text style={[styles.phoneDisclaimerText, { color: '#6b7280' }]}>
                                            <Unicon name="pause-circle-outline" size={12} colorVariant="textSecondary" /> SMS is paused for this number. Check the box to re-enable without sending another opt-in message.
                                        </Text>
                                    </View>
                                )}
                                <View style={styles.phoneToggleRow}>
                                    <View style={styles.phoneToggleItem}>
                                        <Checkbox
                                            testID="sms-opt-in-checkbox"
                                            checked={personDetails.Phones[0]?.smsOptInStatus === 'opted_in'}
                                            onChange={(val) => updatePhoneSMS(val)}
                                            disabled={personDetails.Phones[0]?.smsOptInStatus === 'opted_out' || personDetails.Phones[0]?.smsOptInStatus === 'non_mobile'}
                                        />
                                        <View style={styles.phoneToggleText}>
                                            <Unicon name="chatbubble-ellipses-outline" size={16} colorVariant="textSecondary" />
                                            <Text style={styles.phoneToggleLabel}>Use this number for SMS communications</Text>
                                        </View>
                                    </View>
                                </View>
                                {personDetails.Phones[0]?.smsOptInStatus === 'opted_in' && (
                                    <View style={styles.phoneOptInRow}>
                                        <Text style={styles.phoneDisclaimerText}>
                                            By checking this box, you agree that {personDetails.firstName} {personDetails.lastName} has consented to receive recurring automated text messages from Reach at {personDetails.Phones[0]?.phoneNumber || 'this number'}.{' '}
                                            Consent is not a condition of purchase. Msg & data rates may apply. Reply STOP to cancel. Reply HELP for help. View our{' '}
                                            <Text style={styles.phoneDisclaimerLink} onPress={() => Linking.openURL('https://www.reachchms.com/privacy-policy')}>Privacy Policy</Text>
                                            {' '}and{' '}
                                            <Text style={styles.phoneDisclaimerLink} onPress={() => Linking.openURL('https://www.reachchms.com/terms')}>Terms of Service</Text>
                                            .
                                        </Text>
                                    </View>
                                )}
                            </View>
                        )}
                    </View>
                )}

                {isFieldEnabled('dateOfBirth') && (
                    <DatePickerInput
                        title="Date of Birth"
                        mode="date"
                        value={personDetails.dateOfBirth}
                        onUpdate={(value) => updateField('dateOfBirth', value)}
                    />
                )}

                {isFieldEnabled('gender') && (
                    <SelectInput
                        title="Gender"
                        value={personDetails.gender}
                        options={["Male", "Female"]}
                        onUpdate={(value) => updateField('gender', value)}
                    />
                )}

                {isFieldEnabled('invitedBy') && (
                    <View>
                        <SheetSelector
                            type="people"
                            title="Invited By"
                            value={personDetails.InvitedBy}
                            onChange={(selected) => {
                                setPersonDetails(prev => ({
                                    ...prev,
                                    invitedByID: selected?.id,
                                    InvitedBy: selected
                                }));
                            }}
                        />
                        {familyCopySuggestion(
                            'invitedBy',
                            familySource?.InvitedBy?.id ? familySource.InvitedBy : null,
                            person => person.invitedByID || person.InvitedBy,
                            (person, InvitedBy) => ({ ...person, invitedByID: InvitedBy.id, InvitedBy }),
                            [familySource?.InvitedBy?.firstName, familySource?.InvitedBy?.lastName].filter(Boolean).join(' ')
                        )}
                    </View>
                )}

                {isFieldEnabled('address') && (
                    <View>
                        <SheetSelector
                            type="addresses"
                            title="Address"
                            fromScreen={fromScreen}
                            fromStoreKey={fromStoreKey}
                            addScreenName="addresses/create"
                            value={personDetails.Address}
                            onChange={(selected) => {
                                setPersonDetails(prev => ({
                                    ...prev,
                                    addressID: selected?.id,
                                    Address: selected
                                }));
                            }}
                        />
                        {familyCopySuggestion(
                            'address',
                            familySource?.Address?.id ? familySource.Address : null,
                            person => person.addressID || person.Address,
                            (person, Address) => ({ ...person, addressID: Address.id, Address }),
                            [familySource?.Address?.street, familySource?.Address?.streetLine2, familySource?.Address?.city, familySource?.Address?.state, familySource?.Address?.zip].filter(Boolean).join(', ')
                        )}
                    </View>
                )}

                {isFieldEnabled('family') && showFamily && (
                    <InputSection title="Family" description="Add this person to a family or create a new one">
                        {!personDetails.Family && familyMode === null ? (
                            <View style={styles.familyOptionsContainer}>
                                <Text style={styles.familyHelpText}>
                                    This person is not currently part of a family. Choose an option below:
                                </Text>

                                <Pressable 
                                    testID="Family-Create"
                                    accessibilityRole="button"
                                    style={styles.familyOptionCard}
                                    onPress={() => {
                                        const thisPerson = {
                                            id: undefined,
                                            firstName: personDetails.firstName || 'New Person',
                                            lastName: personDetails.lastName || '',
                                            familyRole: 'Head of Household'
                                        };
                                        setPersonDetails(prev => ({
                                            ...prev,
                                            Family: { FamilyMembers: [thisPerson] }
                                        }));
                                    }}
                                >
                                    <View style={styles.familyOptionIcon}>
                                        <Unicon name="home-outline" size={22} colorVariant="people" />
                                    </View>
                                    <View style={styles.familyOptionContent}>
                                        <Text style={styles.familyOptionTitle}>Create New Family</Text>
                                        <Text style={styles.familyOptionDescription}>
                                            Start a new family with this person as the head of household
                                        </Text>
                                    </View>
                                    <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                                </Pressable>

                                <Pressable 
                                    testID="Family-Join"
                                    accessibilityRole="button"
                                    style={styles.familyOptionCard}
                                    onPress={() => setFamilyMode('join')}
                                >
                                    <View style={styles.familyOptionIcon}>
                                        <Unicon name="people-outline" size={22} colorVariant="people" />
                                    </View>
                                    <View style={styles.familyOptionContent}>
                                        <Text style={styles.familyOptionTitle}>Join Existing Family</Text>
                                        <Text style={styles.familyOptionDescription}>
                                            Add this person to an existing family by selecting a current member
                                        </Text>
                                    </View>
                                    <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                                </Pressable>
                            </View>
                        ) : !personDetails.Family && familyMode === 'join' ? (
                            <View style={styles.familyOptionsContainer}>
                                <View style={styles.familyMembersHeader}>
                                    <Text style={styles.familyMembersTitle}>Join Existing Family</Text>
                                    <SolidButton 
                                        text="Cancel" 
                                        type="secondary" 
                                        variant="outlined" 
                                        size="sm" 
                                        onPress={() => setFamilyMode(null)}
                                    />
                                </View>
                                <Text style={styles.familyHelpText}>
                                    Select a person who is already in a family. This person will be added to their family.
                                </Text>
                                <SheetSelector
                                    type="people"
                                    title="Select Family Member"
                                    placeholder="Search for a person in a family..."
                                    errorFn={(person) => !person.familyID ? ["Not in a family"] : null}
                                    disableErroredOptions
                                    onChange={(selectedPerson) => {
                                        handleJoinExistingFamily(selectedPerson);
                                        setFamilyMode(null);
                                    }}
                                />
                            </View>
                        ) : (
                            <View>
                                <View style={styles.familyMembersHeader}>
                                    <View style={styles.familyTitleRow}>
                                        <Unicon name="people-outline" size={18} colorVariant="people" />
                                        <Text style={styles.familyMembersTitle}>Family Members</Text>
                                        <Text style={styles.familyMemberCount}>{personDetails.Family?.FamilyMembers?.length || 0}</Text>
                                    </View>
                                    <SolidButton 
                                        text="Leave Family"
                                        testID="Family-Leave"
                                        type="error"
                                        variant="muted"
                                        size="sm" 
                                        onPress={() => setPersonDetails(prev => ({ ...prev, Family: null }))}
                                    />
                                </View>

                                <View style={styles.familyMemberList}>
                                    {personDetails.Family?.FamilyMembers?.map((familyMember, idx) => (
                                        <FamilyMemberRow
                                            key={familyMember.id || idx}
                                            profile={familyMember}
                                            testID={`FamilyMember-${familyMember.id || idx}`}
                                            removeDisabled={!familyMember.id}
                                            isCurrentPerson={!familyMember.id}
                                            isLast={idx === personDetails.Family.FamilyMembers.length - 1}
                                            relationshipInput={<RelationshipInput {...familyMember} />}
                                            onRemove={() => {
                                                const remaining = personDetails.Family?.FamilyMembers?.filter((_, i) => i !== idx);
                                                if (remaining.length === 0) {
                                                    setPersonDetails(prev => ({ ...prev, Family: null }));
                                                } else {
                                                    setPersonDetails(prev => ({
                                                        ...prev,
                                                        Family: { ...prev.Family, FamilyMembers: remaining }
                                                    }));
                                                }
                                            }}
                                        />
                                    ))}
                                </View>

                                <View style={styles.addFamilyMemberActions}>
                                    <Text style={styles.familyMembersTitle}>Add a family member</Text>
                                    <SheetSelector 
                                        type="people" 
                                        title="Add Existing Person"
                                        placeholder="Search for a person..."
                                        excludeIds={personDetails.Family?.FamilyMembers?.map(fm => fm.id).filter(Boolean)}
                                        errorFn={(person) => person.Family && person.Family.id !== personDetails.Family?.id ? ["Already in another family"] : null}
                                        disableErroredOptions
                                        onChange={(selectedPerson) => {
                                            const newMember = {
                                                id: selectedPerson.id,
                                                firstName: selectedPerson.firstName,
                                                lastName: selectedPerson.lastName,
                                                familyRole: 'Unassigned'
                                            };
                                            setPersonDetails(prev => ({
                                                ...prev,
                                                Family: {
                                                    ...prev.Family,
                                                    FamilyMembers: [...(prev.Family?.FamilyMembers || []), newMember]
                                                }
                                            }));
                                        }}
                                    />

                                    <View>
                                        <View style={styles.separator}></View>
                                        <View style={{alignItems: 'center'}}><Text style={styles.separatorText}>OR</Text></View>
                                    </View>

                                    <View>
                                        <SolidButton 
                                            text="Quick Add New Person"
                                            testID="Family-QuickAdd"
                                            type="people"
                                            variant="muted"
                                            size="md"
                                            iconName="add-circle-outline" 
                                            iconBefore
                                            onPress={() => setShowQuickAddFamily(true)}
                                        />
                                    </View>
                                </View>
                            </View>
                        )}
                    </InputSection>
                )}

                {!isFree && isFieldEnabled('roles') && loggedInUser?.isAdmin && (
                    <InputSection title="Roles" description="Assign roles to this person">
                        <SheetSelector 
                            multiple 
                            type="roles" 
                            title="Roles" 
                            value={personDetails.Roles || []} 
                            onChange={(selected) => updateField('Roles', selected)} 
                        />
                    </InputSection>
                )}

                {enabledCustomFields.map(field => (
                    <View key={field.id}>
                        <CustomField
                            fieldType={field.type}
                            onUpdate={(value) => {
                                setPersonDetails(prev => ({
                                    ...prev,
                                    customFieldValues: {
                                        ...prev.customFieldValues,
                                        [field.id]: value
                                    }
                                }));
                            }}
                            options={{
                                title: field.name,
                                value: personDetails.customFieldValues?.[field.id],
                                options: field.options
                            }}
                        />
                        {familyCopySuggestion(
                            `custom-${field.id}`,
                            familySource?.customFieldValues?.[field.id],
                            person => person.customFieldValues?.[field.id],
                            (person, value) => ({ ...person, customFieldValues: { ...person.customFieldValues, [field.id]: value } }),
                            formatFamilyValue(familySource?.customFieldValues?.[field.id], field.type)
                        )}
                    </View>
                ))}

                <View style={styles.buttonRow}>
                    {showAddAnother && (
                        <SolidButton
                            text="Create & Add Another"
                            variant="outlined"
                            loading={savePersonMutation.isPending && addAnotherRef.current}
                            disabled={savePersonMutation.isPending}
                            onPress={() => handleSubmit(true)}
                            style={styles.button}
                        />
                    )}
                    <SolidButton
                        text={buttonText}
                        type="primary"
                        loading={savePersonMutation.isPending && !addAnotherRef.current}
                        disabled={savePersonMutation.isPending}
                        onPress={() => handleSubmit(false)}
                        style={styles.button}
                    />
                </View>

                <QuickFormFieldsModal
                    isOpen={showFieldsModal}
                    onClose={() => setShowFieldsModal(false)}
                />

                <SheetModal
                    isOpen={showQuickAddFamily}
                    onClose={() => setShowQuickAddFamily(false)}
                    title="Add Family Member"
                    stayOpenOnBlur
                >
                    <AddPersonQuickForm
                        fromScreen={fromScreen}
                        fromStoreKey={fromStoreKey}
                        formKey={`${formKey}-nested`}
                        familySource={personDetails}
                        onSuccess={handleQuickAddFamilyMember}
                        onCancel={() => setShowQuickAddFamily(false)}
                        buttonText="Add to Family"
                        showCustomizeButton={false}
                        showAddAnother={false}
                        showFamily={false}
                    />
                </SheetModal>

                <DuplicateReviewModal
                    personId={reviewPersonId}
                    onClose={() => setReviewPersonId(null)}
                />
            </View>
            </KeyboardAwareScrollView>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }, rt) => ({
    scrollContent: {
        flexGrow: 1,
        paddingBottom:100
    },
    container: {
        gap: 8,
    },
    familyCopySuggestion: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 8,
        paddingVertical: 8,
        minHeight: 44,
    },
    familyCopyText: {
        flex: 1,
        fontFamily: fonts.body,
        fontSize: 13,
        color: colors.primary,
    },
    duplicateWarningContainer: {
        borderWidth: 1,
        borderColor: colors.warning,
        borderRadius: 12,
        backgroundColor: colors.warningContainer || colors.surface,
        padding: 12,
        gap: 8,
    },
    duplicateWarningHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    duplicateWarningTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    duplicateWarningTitle: {
        fontFamily: fonts.bold,
        fontSize: 14,
        color: colors.warning,
    },
    duplicateWarningSubtitle: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: 16,
    },
    duplicateWarningIgnoreBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 8,
        backgroundColor: colors.surface,
    },
    duplicateWarningIgnoreText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
    },
    customizeButton: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-end',
        gap: 4,
        paddingVertical: 4,
        paddingHorizontal: 8,
        marginBottom: 4,
    },
    customizeButtonText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.primary,
    },
    row: {
        flexDirection: 'row',
        gap: 10,
    },
    halfInput: {
        flex: 1,
    },
    phoneToggleRow: {
        flexDirection: 'row',
        marginTop: 10,
        paddingTop: 10,
    },
    phoneToggleItem: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 12,
    },
    phoneToggleText: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    phoneToggleLabel: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    phoneOptInRow: {
        marginTop: 8,
    },
    phoneDisclaimerText: {
        flex: 1,
        fontSize: 11,
        fontFamily: fonts.body,
        color: colors.textTertiary,
        lineHeight: 16,
    },
    phoneDisclaimerLink: {
        fontSize: 11,
        fontFamily: fonts.body,
        color: colors.primary,
    },
    buttonRow: {
        justifyContent: 'flex-end',
        flexDirection: rt.isLandscape ? 'row' : 'row-reverse',
        gap: 10,
        marginTop: 8,
    },
    button: {
        minWidth: 100,
    },
    familyOptionsContainer: {
        gap: 12,
        paddingTop: 8,
    },
    familyHelpText: {
        color: colors.textSecondary,
        fontSize: 13,
        lineHeight: 19,
        fontFamily: fonts.body,
    },
    familyOptionCard: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        backgroundColor: colors.background,
        borderRadius: 12,
        gap: 12,
        _web: {
            _hover: { backgroundColor: colors.hover },
            '_focus-visible': { outlineColor: colors.people, outlineStyle: 'solid', outlineWidth: 2 },
        },
    },
    familyOptionIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: colors.peopleContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    familyOptionContent: {
        flex: 1,
        gap: 4,
    },
    familyOptionTitle: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    familyOptionDescription: {
        fontSize: 13,
        lineHeight: 19,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    familyMembersHeader: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 8,
        marginBottom: 12,
    },
    familyTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    familyMemberCount: {
        fontSize: 12,
        fontFamily: fonts.heading,
        fontVariant: ['tabular-nums'],
        color: colors.textSecondary,
        backgroundColor: colors.background,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 8,
    },
    familyMembersTitle: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    familyMemberList: {
        backgroundColor: colors.background,
        borderRadius: 12,
    },
    addFamilyMemberActions: {
        gap: 12,
        padding: 16,
        backgroundColor: colors.background,
        borderRadius: 12,
        marginTop: 16,
    },
    separator: {
        height: 1,
        backgroundColor: colors.borderSubtle,
    },
    separatorText: {
        color: colors.textTertiary,
        fontSize: 11,
        fontFamily: fonts.body,
        textAlign: 'center',
        marginTop: -8,
        paddingHorizontal: 10,
        backgroundColor: colors.background,
    },
}));

export default AddPersonQuickForm;
